/* Lunai rail — click-to-scroll arrows for the moments swipe rail.
   The pointer's equivalent of the touch swipe: prev/next scroll the
   snap track by a "page" that keeps one card in view for continuity,
   and CSS scroll-snap does the fine alignment. The buttons ship
   [hidden]; the component reveals them only while the track actually
   overflows, and mirrors the ends into [disabled].
   Listeners bind per-connect through an AbortController: the theme
   editor MOVES the section node on drag-reorder (disconnect+reconnect
   on the same instance), and without the abort each reorder would
   stack a duplicate click listener. */
if (!customElements.get('lunai-rail')) {
  class LunaiRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.prev = this.querySelector('.lunai-rail__arrow--prev');
      this.next = this.querySelector('.lunai-rail__arrow--next');
      if (!this.track || !this.prev || !this.next) return;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.prev.addEventListener('click', () => this.page(-1), { signal });
      this.next.addEventListener('click', () => this.page(1), { signal });
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = 0;
          this.sync();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
      this.ro = new ResizeObserver(this.onScroll);
      this.ro.observe(this.track);
      this.sync();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.ro) this.ro.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    /* One card + gap is the scroll unit; a page is (visible − 1) units
       so the last card of one view opens the next. */
    step() {
      const card = this.track.querySelector('.lunai-tile');
      if (!card) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const unit = card.getBoundingClientRect().width + gap;
      const visible = Math.max(1, Math.floor(this.track.clientWidth / unit));
      return Math.max(unit, (visible - 1) * unit);
    }

    page(dir) {
      this.track.scrollBy({
        left: dir * this.step(),
        behavior: this.reduce.matches ? 'auto' : 'smooth',
      });
    }

    /* Disabling the button the keyboard is sitting on would drop focus
       to <body>; hand it to the opposite arrow first. */
    setDisabled(btn, other, on) {
      if (on && !btn.disabled && document.activeElement === btn && !other.disabled) other.focus();
      btn.disabled = on;
    }

    sync() {
      const t = this.track;
      const overflow = t.scrollWidth > t.clientWidth + 1;
      this.prev.hidden = this.next.hidden = !overflow;
      if (!overflow) return;
      const max = t.scrollWidth - t.clientWidth;
      this.setDisabled(this.prev, this.next, t.scrollLeft <= 1);
      this.setDisabled(this.next, this.prev, t.scrollLeft >= max - 1);
    }
  }
  customElements.define('lunai-rail', LunaiRail);
}
